export const STORE_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
} as const;

export type StoreStatusType = typeof STORE_STATUS[keyof typeof STORE_STATUS];

export const STORE_STATUS_OPTIONS: StoreStatusType[] = [
  STORE_STATUS.ACTIVE,
  STORE_STATUS.INACTIVE,
];

// "Outside Approved – Redeploy": the store is still trading and its employee still
// marks attendance there, but it is outside the approved list, so they must be moved.
// It is a DISPLAY status only — never a stores.store_status value. The column stays
// 'Active' (the mobile store lookup and ~30 other queries match exactly that) and a
// separate redeploy_pending flag carries the state; the API accepts this label on a
// write and sends it back as `status_display` on GET /stores.
// The dash is an EN DASH (U+2013) with a space each side.
export const STORE_STATUS_REDEPLOY = 'Outside Approved – Redeploy' as const;

/** What the Status field can read on Master Data → Stores. */
export type StoreDisplayStatusType = StoreStatusType | typeof STORE_STATUS_REDEPLOY;

// The Stores page's Status choices, in the order its selects list them. Kept apart
// from STORE_STATUS_OPTIONS, which stays the two values store_status really holds —
// the store-request approval flow accepts only those.
export const STORE_DISPLAY_STATUS_OPTIONS: StoreDisplayStatusType[] = [
  STORE_STATUS.ACTIVE,
  STORE_STATUS_REDEPLOY,
  STORE_STATUS.INACTIVE,
];

/** The Status a GET /stores row should show. An API that predates `status_display`
 *  sends only the stored value, and older rows can have that blank (NULL, '' or
 *  spaces) — those read as Active. */
export const storeDisplayStatus = (
  store: { status_display?: string | null; store_status?: string | null },
): string => store.status_display?.trim() || store.store_status?.trim() || STORE_STATUS.ACTIVE;

// Badge colours for the Status column. Active and Inactive keep the colours the old
// `=== 'Active' ? green : yellow` ternary gave them, and so does any other stored
// value (production also carries 'Notice') through the fallback.
export const STORE_STATUS_BADGE: Record<string, string> = {
  [STORE_STATUS.ACTIVE]: 'border-green-200 bg-green-100 text-green-800',
  [STORE_STATUS_REDEPLOY]: 'border-violet-200 bg-violet-100 text-violet-800',
  [STORE_STATUS.INACTIVE]: 'border-yellow-200 bg-yellow-100 text-yellow-800',
};

export const STORE_STATUS_OTHER_BADGE = 'border-yellow-200 bg-yellow-100 text-yellow-800';

// Staffing status is a SECOND, independent axis from store_status — it describes
// where the store sits in the manpower pipeline, not whether the store is part of
// the footprint. A store is routinely Active AND 'Hiring to be done' at the same
// time. Mirrors apps/api/src/constants/storeStatus.ts; the two files are
// duplicated rather than shared, as the existing STORE_STATUS pair already is.
export const STAFFING_STATUS = {
  CATCH_UP_PENDING: 'Catch Up pending',
  DEPLOYED: 'Deployed',
  HIRING_ON_HOLD: 'Hiring is on Hold',
  // The store does not need anyone hired: set by a person, never by the automatic
  // Deployed / Hiring to be done recompute, which leaves it alone.
  HIRING_NOT_NEEDED: 'Hiring not needed',
  HIRING_TO_BE_DONE: 'Hiring to be done',
  SHORTLISTED_OFFERED: 'Shortlisted / Offered',
  TAGGED_INTERNAL_SHUFFLING: 'Tagged for Internal Shuffling',
} as const;

export type StaffingStatusType = typeof STAFFING_STATUS[keyof typeof STAFFING_STATUS];

export const STAFFING_STATUS_OPTIONS: StaffingStatusType[] = [
  STAFFING_STATUS.CATCH_UP_PENDING,
  STAFFING_STATUS.DEPLOYED,
  STAFFING_STATUS.HIRING_ON_HOLD,
  STAFFING_STATUS.HIRING_NOT_NEEDED,
  STAFFING_STATUS.HIRING_TO_BE_DONE,
  STAFFING_STATUS.SHORTLISTED_OFFERED,
  STAFFING_STATUS.TAGGED_INTERNAL_SHUFFLING,
];

// Badge colours for the staffing column. Deliberately a lookup map rather than
// the `=== 'Active' ? green : yellow` ternary the store_status badges used to use —
// that pattern paints every unrecognised value yellow, which is the wrong default
// once a column has seven legitimate values. 'Hiring not needed' is a quiet slate,
// a step darker than the grey of a store with no staffing status, so the two never
// read as the same thing.
export const STAFFING_STATUS_BADGE: Record<string, string> = {
  [STAFFING_STATUS.DEPLOYED]: 'border-green-200 bg-green-100 text-green-800',
  [STAFFING_STATUS.SHORTLISTED_OFFERED]: 'border-sky-200 bg-sky-100 text-sky-800',
  [STAFFING_STATUS.HIRING_TO_BE_DONE]: 'border-amber-200 bg-amber-100 text-amber-800',
  [STAFFING_STATUS.CATCH_UP_PENDING]: 'border-orange-200 bg-orange-100 text-orange-800',
  [STAFFING_STATUS.HIRING_ON_HOLD]: 'border-rose-200 bg-rose-100 text-rose-800',
  [STAFFING_STATUS.HIRING_NOT_NEEDED]: 'border-slate-300 bg-slate-200 text-slate-700',
  [STAFFING_STATUS.TAGGED_INTERNAL_SHUFFLING]: 'border-violet-200 bg-violet-100 text-violet-800',
};

export const STAFFING_STATUS_UNSET_BADGE = 'border-gray-200 bg-gray-100 text-gray-600';
